import { useCallback, useEffect, useRef, useState } from "react";
import { Bot } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useChatContext } from "@/stores/chat-context-store";

type QuickActionKey =
  | "quickActions.checklistRequirement"
  | "quickActions.checklistDocuments"
  | "quickActions.auditReady"
  | "quickActions.auditNextActions"
  | "quickActions.auditMissing"
  | "quickActions.documentAudits"
  | "quickActions.documentRequirements"
  | "quickActions.dashboardUrgent"
  | "quickActions.dashboardOverview"
  | "quickActions.dashboardDeadlines";

interface ChatQuickActionsProps {
  onAction: (text: string) => void;
}

function useRandomGreeting() {
  const { t } = useTranslation("chat");
  const greetings = t("emptyState.greetings", {
    returnObjects: true,
  }) as string[];
  const lastIndexRef = useRef(-1);

  return useCallback(() => {
    if (!Array.isArray(greetings) || greetings.length === 0) return "";
    let idx: number;
    do {
      idx = Math.floor(Math.random() * greetings.length);
    } while (idx === lastIndexRef.current && greetings.length > 1);
    lastIndexRef.current = idx;
    return greetings[idx];
  }, [greetings]);
}

function EmptyPrompt() {
  const { t } = useTranslation("chat");
  const getGreeting = useRandomGreeting();
  const [greeting, setGreeting] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  function handleClick() {
    clearTimeout(timerRef.current);
    setGreeting(getGreeting());
    timerRef.current = setTimeout(() => setGreeting(null), 5000);
  }

  return (
    <div className="flex flex-col items-center text-center gap-3">
      {/* Reserve space for the speech bubble so Nero-Ki doesn't shift */}
      <div className="h-[52px] flex items-end justify-center">
        <AnimatePresence>
          {greeting && (
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="relative rounded-xl bg-accent/10 px-4 py-2.5 text-[13px] text-primary max-w-[260px]"
            >
              <span className="relative">{greeting}</span>
              <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-accent/10 rotate-45 rounded-sm" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <motion.button
        type="button"
        className="h-11 w-11 rounded-xl bg-accent/10 flex items-center justify-center cursor-pointer"
        whileHover={{
          rotate: [0, -12, 12, -8, 8, 0],
          transition: { duration: 0.5 },
        }}
        whileTap={{ scale: 0.92 }}
        onClick={handleClick}
      >
        <Bot className="h-5 w-5 text-accent" />
      </motion.button>

      <p className="text-xs text-muted">{t("emptyState.prompt")}</p>
    </div>
  );
}

function Suggestions({ onAction }: ChatQuickActionsProps) {
  const { t } = useTranslation("chat");
  const ambient = useChatContext((s) => s.ambient);
  const entities = useChatContext((s) => s.entities);

  const actionKeys = getActionKeysForContext(ambient?.page, entities);

  return (
    <div className="px-4 pb-3 flex flex-col gap-1.5">
      {actionKeys.map((key) => {
        const label = t(key);
        return (
          <button
            key={key}
            type="button"
            onClick={() => onAction(String(label))}
            className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg border border-border/60 bg-card/50 hover:bg-card hover:border-border text-left transition-all cursor-pointer"
          >
            <span className="text-[13px] text-secondary hover:text-primary">
              {String(label)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function ChatQuickActions({ onAction }: ChatQuickActionsProps) {
  return (
    <div className="flex flex-col items-center text-center px-6 py-8 gap-5">
      <EmptyPrompt />
      <Suggestions onAction={onAction} />
    </div>
  );
}

ChatQuickActions.EmptyPrompt = EmptyPrompt;
ChatQuickActions.Suggestions = Suggestions;

/** Returns translation keys (NOT translated strings). Caller resolves them with t(). */
function getActionKeysForContext(
  page?: string,
  entities?: Array<{ type: string }>,
): QuickActionKey[] {
  const hasAuditEntity = entities?.some((e) => e.type === "audit");
  const hasDocumentEntity = entities?.some((e) => e.type === "document");
  const hasChecklistEntity = entities?.some((e) => e.type === "checklist-item");

  if (hasChecklistEntity) {
    return [
      "quickActions.checklistRequirement",
      "quickActions.checklistDocuments",
    ];
  }
  if (hasAuditEntity || page === "audits") {
    return [
      "quickActions.auditReady",
      "quickActions.auditNextActions",
      "quickActions.auditMissing",
    ];
  }
  if (hasDocumentEntity || page === "documents") {
    return ["quickActions.documentAudits", "quickActions.documentRequirements"];
  }
  // Dashboard / home / default
  return [
    "quickActions.dashboardUrgent",
    "quickActions.dashboardOverview",
    "quickActions.dashboardDeadlines",
  ];
}
